import { isNil } from "lodash";
import { createListSelector } from "reselect-map";

import tableDropAction from "../constants/tableDropAction";
import tableRowTypes from "../constants/tableRowTypes";
import { isDateValid } from "../modules/definition/calendar/dateFunctions";
import { immutableRecords } from "../types";

const findRow = (rows, rowId) => rows.find(r => r.id === rowId);

const isDropAllowed = (rows, draggedRowIds, dropRowId) => {
  if (draggedRowIds.includes(dropRowId)) {
    return false;
  }
  let dropRow = findRow(rows, dropRowId);
  const maxTraversal = 500;
  let traversalCount = 0;
  while (!isNil(dropRow.parentRowId) && traversalCount < maxTraversal) {
    if (draggedRowIds.includes(dropRow.parentRowId)) {
      return false;
    }
    dropRow = findRow(rows, dropRow.parentRowId);
    traversalCount++;
  }
  return true;
};

const isInRange = (i, min, max, pad = 0) => i >= min + pad && i < max - pad;

const getDropRowOnEmptyRow = rows => rows.findLast(r => r.id !== tableRowTypes.EMPTY && !r.parentRowId);

export const tableDropPositionWithNesting = (
  rows,
  mouseY,
  dragItem,
  canItemDropOnRow,
  yPad,
  disableReordering,
  displayItems
) => {
  // Find what we are hovering over...
  const dropRowIndex = getDropRow(dragItem, rows, mouseY);

  // If drop above/on/below the empty row,
  // always view it as dropping below the last not empty row
  const isDropOnEmpty = dropRowIndex === rows.count() - 1;

  let dropRow = isDropOnEmpty ? getDropRowOnEmptyRow(rows) : rows.get(dropRowIndex);
  let dropAction = isDropOnEmpty
    ? tableDropAction.DropBelowRow
    : getDropAction(rows, dragItem, dropRow, mouseY, yPad, canItemDropOnRow);
  let dropAsChild = null;

  // If reordering is disabled (e.g.not sorted by sequence in spreadsheet), drop in the nearest parent
  if (disableReordering && dropAction !== tableDropAction.DropOnRow) {
    const dropRowItem = displayItems.find(r => {
      if (dropRow) {
        return r.id === dropRow.id;
      }
      return false;
    });

    if (dropRowItem && dropRowItem.super) {
      const dropRowSuperItem = displayItems.find(r => {
        return r.id === dropRowItem.super;
      });
      const dropRowSuperItemRow = rows.find(r => r.id === dropRowSuperItem.id);
      const canItemDropOnSuperRow = canItemDropOnRow(dragItem, dropRowSuperItemRow);

      if (canItemDropOnSuperRow) {
        dropRow = dropRowSuperItemRow;
        dropAction = tableDropAction.DropOnRow;
        dropAsChild = true;
      } else {
        dropAction = tableDropAction.DropTopLevel;
      }
    } else {
      dropAction = tableDropAction.DropTopLevel;
    }
  }

  return { dropRow, dropAction, dropAsChild };
};

export const tableDropRowIndex = (rows, mouseY, dragItem) => {
  const dropRowIndex = getDropRow(dragItem, rows, mouseY);
  return dropRowIndex;
};

export const tableDropRowPreviousStartDate = (rows, dropRowIndex, displayItems) => {
  if (displayItems) {
    let index = dropRowIndex;
    while (index >= 0) {
      const row = rows.get(index);
      const item = displayItems.find(item => item.id === row.id);
      if (item && isDateValid(item.startDate)) {
        return item.startDate;
      }
      index -= 1;
    }
  }

  return null;
};

const toTableRowRecord = createListSelector(
  rows => rows,
  row =>
    immutableRecords.TableRowRecord({
      top: row.rect.top,
      height: row.rect.height,
      id: row.itemDisplay.id,
      index: row.itemDisplay.index,
      type: row.itemDisplay.type,
      hasChildren: row.columnData ? row.columnData.hasChildren : row.hasChildren,
      childrenExpanded: row.childrenExpanded,
      parentRowId: row.itemDisplay.super
    })
);

export const relationshipDropPositionWithNesting = (rows, mouseY, dragItem, canItemDropOnRow, yPad) => {
  const mappedRows = toTableRowRecord(rows);
  const dropRowIndex = getDropRow(dragItem, mappedRows, mouseY);
  const mappedDropRow = mappedRows.get(dropRowIndex);
  const dropRow = rows.get(dropRowIndex);
  const dropAction = getDropAction(mappedRows, dragItem, mappedDropRow, mouseY, yPad, canItemDropOnRow);
  return { dropRow, dropAction };
};

export const relationshipDropRowIndex = (rows, mouseY, dragItem) => {
  const mappedRows = toTableRowRecord(rows);
  const dropRowIndex = getDropRow(dragItem, mappedRows, mouseY);
  return dropRowIndex;
};

export const relationshipDropRowPreviousStartDate = (rows, dropRowIndex) => {
  let index = dropRowIndex;
  while (index >= 0) {
    const row = rows.get(index);
    if (row && row.itemDisplay && isDateValid(row.itemDisplay.startDate)) {
      return row.itemDisplay.startDate;
    }
    index -= 1;
  }

  return null;
};

export const subwayDropRowPreviousStartDate = (nodes, hoverIndex, displayItems) => {
  if (displayItems) {
    let index = hoverIndex;
    while (index >= 0) {
      const node = nodes.get(index);
      if (node) {
        const item = displayItems.find(item => item.id === node.itemId);
        if (item && isDateValid(item.startDate)) {
          return item.startDate;
        }
      }
      index -= 1;
    }
  }

  return null;
};

const getDropAction = (rows, dragItem, dropRow, mouseY, yPad, canItemDropOnRow) => {
  const dragIds = dragItem.multipleItemIds;
  if (dropRow && isDropAllowed(rows, dragIds, dropRow.id)) {
    if (canItemDropOnRow(dragItem, dropRow) && isInRange(mouseY, dropRow.top, dropRow.top + dropRow.height, yPad)) {
      // Hover over middle of item
      // Set parent, but do not have an explicit index...
      return tableDropAction.DropOnRow;
    }
    // Hovering near an edge...
    if (mouseY < dropRow.top + yPad) {
      return tableDropAction.DropAboveRow;
    }
    // If they hover below a row with its children expanded, drop inside rather than
    // outside.
    if (dropRow.hasChildren && dropRow.childrenExpanded && canItemDropOnRow(dragItem, dropRow)) {
      return tableDropAction.DropOnRow;
    }
    return tableDropAction.DropBelowRow;
  }

  return tableDropAction.DropAboveRow;
};

const getDropRow = (dragItem, rows, mouseY) => {
  if (rows.size <= 0) {
    return null;
  }
  const hoverIndex = rows.findIndex(r => isInRange(mouseY, r.top, r.top + r.height));
  const dropRow = rows.get(hoverIndex);
  if (hoverIndex >= 0) {
    if (isDropAllowed(rows, dragItem.multipleItemIds, dropRow.id)) {
      return hoverIndex;
    }
    return rows.findIndex(r => r.id === dragItem.itemId);
  }
  if (mouseY < 0) {
    return 0;
  }
  return rows.count() - 1;
};

export const isDragSettingSuper = (dragItem, modifierKeys) => {
  return (
    (modifierKeys.CONTROL || modifierKeys.COMMAND) &&
    !modifierKeys.SHIFT &&
    !modifierKeys.ALT &&
    !dragItem.draggingEndArrow &&
    !dragItem.draggingStartArrow
  );
};

export const isDragMeasuringTimespan = dragItem => {
  return dragItem.initialValues.dragStartModifierKeys
    ? dragItem.initialValues.dragStartModifierKeys.ALT && dragItem.initialValues.dragStartModifierKeys.SHIFT
    : false;
};
